const DEFAULT_PUBLIC_SITE_URL = 'https://www.tabbio.com';
const DEFAULT_APP_SITE_URL = 'https://app.tabbio.com';
const DEFAULT_PUBLIC_DOMAIN = 'tabbio.com';
// A handle may start with a letter OR a digit. Phone-number-style handles
// (e.g. "0501414197ff") are real, registered CVs, but a letter-only first
// character silently rejected them: getPublicHandleFromHostname then returned
// null, the proxy skipped its subdomain branch, and `{handle}.tabbio.com`
// served a full, indexable duplicate of the marketing site instead of the CV.
//
// Length is 3–63: the maximum length of a single DNS label, which is the real
// constraint on a `{handle}.tabbio.com` subdomain. It was 3–39, borrowed from
// USERNAME_MAX_LENGTH, but company career pages do not use the username policy -
// `companySlugSchema` allows 48 characters. A 40-to-48-character company slug
// was therefore accepted at creation and then unroutable: isPublicHandleLike
// returned false, so the proxy skipped its subdomain branch and locale-prefixed
// the path into a 404, while the matching subdomain was treated as a
// non-canonical wildcard and bounced to the www root. This regex governs
// routing only; USERNAME_MAX_LENGTH stays 39 as a deliberate product cap on
// what a person may register.
//
// Still: alphanumeric start/end, no leading/trailing hyphen and no "--" (that
// sequence is reserved for job slugs); reserved labels are excluded separately
// by isPublicHandleLike.
const HANDLE_SEGMENT_REGEX = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

export const RESERVED_PUBLIC_SUBDOMAINS = [
  'www',
  'app',
  'api',
  'server',
  'v2',
  'beta',
  'beta-api',
  'staging',
  'admin',
  // The partner portal is its own app on partners.tabbio.com. Reserving the
  // label keeps it out of the handle space so no user or company can claim it
  // and shadow the host through wildcard DNS.
  'partners',
  'mail',
  'cdn',
  'static',
  'assets',
  'jobs',
  'companies',
  'support',
  'help',
  // Public skill share pages (skills.tabbio.com/<skillId>); reserving both
  // labels keeps the singular from ever resolving as a profile handle.
  'skills',
  'skill',
  // The public CV Health check (cvhealth.tabbio.com, served by apps/web). The
  // hyphenated spelling is reserved with it so neither can become a handle
  // that reads as the first-party check.
  'cvhealth',
  'cv-health',
  // apps/docs and apps/changelog run on their own hosts. Both labels were
  // already blocked at signup by RESERVED_USERNAMES, so handle registration
  // was never exposed - but this list is what governs host semantics, and a
  // label that names a first-party deployment must not be handle-like here
  // either. Keeping the two lists in agreement is what stops a subdomain from
  // being routed as a profile.
  'docs',
  'changelog',
  // The developer portal (apps/developer) runs on developer.tabbio.com. The two
  // near-misses are reserved with it: a handle at developers.* or dev.* would
  // read as a first-party developer surface without being one.
  'developer',
  'developers',
  'dev',
  // The developer platform (apps/platform) runs on platform.tabbio.com, and the
  // admin console (apps/admin) on console.tabbio.com. `dashboard` is the
  // near-miss reserved with them: a handle there would read as a first-party
  // control surface without being one.
  'platform',
  'console',
  'dashboard',
] as const;

const RESERVED_PUBLIC_SUBDOMAIN_SET = new Set<string>(
  RESERVED_PUBLIC_SUBDOMAINS,
);

export type PublicJobSlugKind = 'native' | 'external';
export type PublicProfileUrlOwnerKind = 'user' | 'company';

export interface ParsedPublicJobSlug {
  kind: PublicJobSlugKind;
  id: string;
  catalogJobId: string;
}

export interface PublicUserUrlSet {
  hub: string;
  cv: string;
  resumeJson: string;
  displayHandle: string;
}

export interface PublicCompanyUrlSet {
  hub: string;
  jobs: string;
  displayHandle: string;
}

export type PublicProfileUrlSet = PublicUserUrlSet | PublicCompanyUrlSet;

export interface PublicJobSlugInput {
  publicSlug?: string | null;
  id: string;
  title: string;
  isExternal?: boolean;
  company?: {
    name?: string | null;
  } | null;
  /**
   * Location, when the record carries one. It is part of the slug because ATS
   * feeds post one role per location under an identical title: nine Mindrift
   * "Ex-MBB Strategy Consultant" requisitions across nine countries, five Apt
   * Resources technician roles across five Saudi cities. Each is a real separate
   * opening with its own upstream apply URL, so they must stay separate URLs -
   * but without the location they were separate URLs sharing one slug prefix and
   * one title tag, which is a near-duplicate set rather than nine pages.
   */
  city?: string | null;
  country?: string | null;
}

function normalizeBaseUrl(baseUrl: string | undefined, fallback: string) {
  const clean = typeof baseUrl === 'string' && baseUrl.trim().length > 0 ? baseUrl.trim() : fallback;
  return clean.endsWith('/') ? clean.slice(0, -1) : clean;
}

function getLocalPublicBaseUrl(baseUrl: string | undefined) {
  const normalized = normalizeBaseUrl(baseUrl, DEFAULT_PUBLIC_SITE_URL);

  try {
    const parsed = new URL(
      normalized.startsWith('http://') || normalized.startsWith('https://')
        ? normalized
        : `https://${normalized}`,
    );
    const hostname = parsed.hostname.toLowerCase();
    if (
      hostname === 'localhost' ||
      hostname.endsWith('.localhost') ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname === '::1'
    ) {
      return parsed.origin;
    }
  } catch {
    return null;
  }

  return null;
}

function normalizePath(pathname: string) {
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function getPublicRootDomain(value = DEFAULT_PUBLIC_DOMAIN) {
  const clean = normalizeBaseUrl(value, DEFAULT_PUBLIC_DOMAIN)
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .toLowerCase();
  if (!clean) {
    return DEFAULT_PUBLIC_DOMAIN;
  }

  try {
    const parsed = new URL(
      clean.includes('://') ? clean : `https://${clean}`,
    );
    // Strip a leading `www.` so profile subdomains are always built on the
    // registrable domain, regardless of whether the configured site URL uses
    // www. Without this, a base of `https://www.tabbio.com` would produce
    // `{handle}.www.tabbio.com` instead of `{handle}.tabbio.com` (and break
    // subdomain handle detection the same way). `www` is a reserved subdomain,
    // so it is never a real profile root.
    return parsed.host.toLowerCase().replace(/^www\./, '');
  } catch {
    return clean.replace(/^www\./, '');
  }
}

export function normalizePublicHandle(handle: string | null | undefined) {
  return typeof handle === 'string'
    ? handle.trim().replace(/^@+/, '').replace(/_/g, '-').toLowerCase()
    : '';
}

export function isPublicHandleLike(handle: string | null | undefined) {
  const normalized = normalizePublicHandle(handle);
  return (
    HANDLE_SEGMENT_REGEX.test(normalized) &&
    !normalized.includes('--') &&
    !isReservedPublicSubdomain(normalized)
  );
}

export function isReservedPublicSubdomain(value: string | null | undefined) {
  const normalized = normalizePublicHandle(value);
  return RESERVED_PUBLIC_SUBDOMAIN_SET.has(normalized);
}

export function buildCanonicalProfileUrl(handle: string, baseUrl = DEFAULT_PUBLIC_SITE_URL) {
  const normalized = normalizePublicHandle(handle);
  return `${normalizeBaseUrl(baseUrl, DEFAULT_PUBLIC_SITE_URL)}/${encodeURIComponent(normalized)}`;
}

export function buildPersonHubUrl(handle: string, baseUrl = DEFAULT_PUBLIC_SITE_URL) {
  return buildCanonicalProfileUrl(handle, baseUrl);
}

export function buildPrettyProfileUrl(handle: string, domain = DEFAULT_PUBLIC_DOMAIN) {
  return buildPersonCvUrl(handle, domain);
}

export function buildPersonCvUrl(
  handle: string,
  domain = DEFAULT_PUBLIC_DOMAIN,
  locale?: string | null,
) {
  const normalized = normalizePublicHandle(handle);
  const localPublicBaseUrl = getLocalPublicBaseUrl(domain);
  const normalizedLocale = typeof locale === 'string' ? locale.trim().replace(/^\/+|\/+$/g, '') : '';
  if (localPublicBaseUrl) {
    return `${localPublicBaseUrl}/${encodeURIComponent(normalized)}/cv`;
  }

  const rootDomain = getPublicRootDomain(domain);
  const base = `https://${normalized}.${rootDomain}`;
  return normalizedLocale ? `${base}/${encodeURIComponent(normalizedLocale)}` : base;
}

/**
 * Stable public URL for one tailored CV owned by a person handle.
 *
 * The person's root subdomain remains the canonical main WebCV. Tailored CVs
 * live below `/cv/:slug`, while local development keeps the handle in the
 * pathname because wildcard localhost subdomains are not assumed.
 */
export function buildPersonTailoredCvUrl(
  handle: string,
  slug: string,
  domain = DEFAULT_PUBLIC_DOMAIN,
) {
  const normalizedHandle = normalizePublicHandle(handle);
  const normalizedSlug = slug.trim();
  const localPublicBaseUrl = getLocalPublicBaseUrl(domain);
  if (localPublicBaseUrl) {
    return `${localPublicBaseUrl}/${encodeURIComponent(normalizedHandle)}/cv/${encodeURIComponent(normalizedSlug)}`;
  }

  return `${buildPersonCvUrl(normalizedHandle, domain)}/cv/${encodeURIComponent(normalizedSlug)}`;
}

export interface SplitCvHandle {
  /** The leading subdomain segment, e.g. "jane" in "jane.tabbio.com". */
  handle: string;
  /** The remaining domain suffix including the leading dot, e.g. ".tabbio.com". */
  suffix: string;
}

/**
 * Splits a public CV URL into its handle and domain suffix for two-tone display
 * (handle in the text color, suffix muted). Strips any protocol first and splits
 * at the first dot; a dotless value returns the whole string as the handle.
 */
export function splitCvHandle(url: string | null | undefined): SplitCvHandle {
  const clean = (url ?? '').replace(/^https?:\/\//, '');
  const dotIdx = clean.indexOf('.');
  if (dotIdx === -1) return { handle: clean, suffix: '' };
  return { handle: clean.slice(0, dotIdx), suffix: clean.slice(dotIdx) };
}

export function buildPersonCvLocaleUrl(
  handle: string,
  locale: string,
  domain = DEFAULT_PUBLIC_DOMAIN,
) {
  return buildPersonCvUrl(handle, domain, locale);
}

export function buildCanonicalCvUrl(handle: string, domain = DEFAULT_PUBLIC_DOMAIN) {
  return buildPersonCvUrl(handle, domain);
}

export function buildPersonCvPathUrl(handle: string, baseUrl = DEFAULT_PUBLIC_SITE_URL) {
  return `${buildCanonicalProfileUrl(handle, baseUrl)}/cv`;
}

export function buildPersonResumeJsonUrl(
  handle: string,
  domain = DEFAULT_PUBLIC_DOMAIN,
) {
  const normalized = normalizePublicHandle(handle);
  const localPublicBaseUrl = getLocalPublicBaseUrl(domain);
  if (localPublicBaseUrl) {
    return `${localPublicBaseUrl}/${encodeURIComponent(normalized)}/resume.json`;
  }

  return `${buildPersonCvUrl(handle, domain)}/resume.json`;
}

export function buildPersonResumeJsonPathUrl(
  handle: string,
  baseUrl = DEFAULT_PUBLIC_SITE_URL,
) {
  return `${buildCanonicalProfileUrl(handle, baseUrl)}/resume.json`;
}

export function buildPersonPrivateCvShareUrl(
  handle: string,
  token: string,
  domain = DEFAULT_PUBLIC_DOMAIN,
) {
  const normalized = normalizePublicHandle(handle);
  const localPublicBaseUrl = getLocalPublicBaseUrl(domain);
  if (localPublicBaseUrl) {
    return `${localPublicBaseUrl}/${encodeURIComponent(normalized)}/s/${encodeURIComponent(token)}`;
  }

  return `${buildPersonCvUrl(handle, domain)}/s/${encodeURIComponent(token)}`;
}

export function buildLegacyCvShareUrl(
  token: string,
  appUrl = DEFAULT_APP_SITE_URL,
) {
  return new URL(
    `/cv-share/${encodeURIComponent(token)}`,
    normalizeBaseUrl(appUrl, DEFAULT_APP_SITE_URL),
  ).toString();
}

export function buildPublicProfileUrls(
  ownerKind: 'user',
  handle: string,
  options?: { publicSiteUrl?: string; publicDomain?: string },
): PublicUserUrlSet;
export function buildPublicProfileUrls(
  ownerKind: 'company',
  handle: string,
  options?: { publicSiteUrl?: string; publicDomain?: string },
): PublicCompanyUrlSet;
export function buildPublicProfileUrls(
  ownerKind: PublicProfileUrlOwnerKind,
  handle: string,
  options: { publicSiteUrl?: string; publicDomain?: string } = {},
): PublicProfileUrlSet {
  const normalized = normalizePublicHandle(handle);
  const publicSiteUrl = options.publicSiteUrl ?? DEFAULT_PUBLIC_SITE_URL;
  const publicDomain = options.publicDomain ?? publicSiteUrl;
  const hub = buildCanonicalProfileUrl(normalized, publicSiteUrl);

  if (ownerKind === 'user') {
    const cv = buildPersonCvUrl(normalized, publicDomain);
    return {
      hub,
      cv,
      resumeJson: buildPersonResumeJsonUrl(normalized, publicDomain),
      displayHandle: normalized,
    };
  }

  return {
    hub,
    jobs: `${hub.replace(/\/$/, '')}/jobs`,
    displayHandle: normalized,
  };
}

export function getPublicHandleFromHostname(
  hostname: string | null | undefined,
  rootDomains: string | string[] = DEFAULT_PUBLIC_DOMAIN,
) {
  const host = (hostname ?? '').split(':')[0]?.toLowerCase() ?? '';
  if (!host) return null;

  const domains = (Array.isArray(rootDomains) ? rootDomains : [rootDomains])
    .map(getPublicRootDomain)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const rootDomain of domains) {
    if (host === rootDomain || !host.endsWith(`.${rootDomain}`)) {
      continue;
    }

    const subdomain = host.slice(0, -(rootDomain.length + 1));
    if (!subdomain || subdomain.includes('.')) {
      continue;
    }

    if (isPublicHandleLike(subdomain)) {
      return normalizePublicHandle(subdomain);
    }
  }

  return null;
}

export function buildAppJobUrl(
  catalogJobId: string,
  baseUrl = DEFAULT_APP_SITE_URL,
  returnTo?: string | null,
) {
  const url = new URL(
    `/jobs/${encodeURIComponent(catalogJobId)}`,
    normalizeBaseUrl(baseUrl, DEFAULT_APP_SITE_URL),
  );
  if (returnTo) {
    url.searchParams.set('returnTo', returnTo);
  }
  return url.toString();
}

// ─── Catalog job route ids ───────────────────────────────────────────
// Catalog job ids are colon-form (`native:<id>` / `external:<id>`), but a
// colon breaks as a URL path segment, so routes, post-auth targets, and the
// apply handoff carry the dash form (`native-<id>` / `external-<id>`). These
// helpers are the single owner of that conversion: apps must not hand-roll
// `replace(':', '-')`.

const CATALOG_JOB_ID_FORMS = /^(native|external)[:-](.*)$/;

/** Strict dash-form route id as it appears in URLs and `jobs:` targets. */
export const CATALOG_JOB_ROUTE_ID_PATTERN = /^(native|external)-[A-Za-z0-9_-]+$/;

/** Catalog id in either form → URL-safe dash form (`native-<id>`). */
export function toCatalogJobRouteId(catalogJobId: string) {
  const match = catalogJobId.match(CATALOG_JOB_ID_FORMS);
  return match ? `${match[1]}-${match[2]}` : catalogJobId;
}

/** Route id in either form → colon-form catalog id (`native:<id>`). */
export function fromCatalogJobRouteId(routeId: string) {
  const match = routeId.match(CATALOG_JOB_ID_FORMS);
  return match ? `${match[1]}:${match[2]}` : routeId;
}

/**
 * Public "Apply" entry for a catalog job. Points at the app's unguarded
 * /jobs-handoff shim: which forks on auth state (login → onboarding → job)
 * and survives the sign-in navigator remount: NOT at /jobs/<id>, which is
 * only reliably reachable with an already-open session.
 */
export function buildAppJobApplyUrl(
  catalogJobId: string,
  baseUrl = DEFAULT_APP_SITE_URL,
) {
  const routeId = toCatalogJobRouteId(catalogJobId);
  return new URL(
    `/jobs-handoff?id=${encodeURIComponent(routeId)}`,
    normalizeBaseUrl(baseUrl, DEFAULT_APP_SITE_URL),
  ).toString();
}

export function buildCanonicalJobUrl(
  slug: string,
  baseUrl = DEFAULT_PUBLIC_SITE_URL,
  locale = 'en',
) {
  // The public jobs board is locale-prefixed (/en/jobs, /ar/jobs); bare /jobs
  // 308s to the default locale, so canonicals must carry the prefix.
  return `${normalizeBaseUrl(baseUrl, DEFAULT_PUBLIC_SITE_URL)}/${locale}/jobs/${encodeURIComponent(slug)}`;
}

function slugifyPublicText(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 80)
    .replace(/-+$/g, '');
}

function getJobSlugParts(job: PublicJobSlugInput) {
  const nativePrefix = 'native:';
  const externalPrefix = 'external:';
  if (job.id.startsWith(nativePrefix)) {
    return { kind: 'native' as const, id: job.id.slice(nativePrefix.length) };
  }
  if (job.id.startsWith(externalPrefix)) {
    return { kind: 'external' as const, id: job.id.slice(externalPrefix.length) };
  }
  return { kind: job.isExternal ? ('external' as const) : ('native' as const), id: job.id };
}

export function buildPublicJobSlug(job: PublicJobSlugInput) {
  const { kind, id } = getJobSlugParts(job);
  const suffix = '--' + (kind === 'native' ? 'n' : 'e') + '_' + encodeURIComponent(id);
  if (job.publicSlug?.endsWith(suffix) && /^[a-z0-9][a-z0-9-]*--[ne]_[a-zA-Z0-9_%.-]+$/.test(job.publicSlug)) {
    return job.publicSlug;
  }
  const companyName = job.company?.name?.trim();
  // City first, falling back to country: "at Mindrift Qatar" distinguishes the
  // nine identically-titled requisitions, and a bare country is still a
  // differentiator when the feed gives no city.
  const place = job.city?.trim() || job.country?.trim() || '';
  const withCompany = companyName ? `${job.title} at ${companyName}` : job.title;
  const base =
    slugifyPublicText(place ? `${withCompany} ${place}` : withCompany) || 'job';
  const suffixKind = kind === 'native' ? 'n' : 'e';
  return `${base}--${suffixKind}_${encodeURIComponent(id)}`;
}

export function parsePublicJobSlug(slug: string): ParsedPublicJobSlug | null {
  const match = slug.match(/--([ne])_([^/]+)$/);
  if (!match) {
    return null;
  }

  const kind: PublicJobSlugKind = match[1] === 'n' ? 'native' : 'external';
  const id = decodeURIComponent(match[2] ?? '').trim();
  if (!id) {
    return null;
  }

  return {
    kind,
    id,
    catalogJobId: `${kind}:${id}`,
  };
}

export function buildPublicPath(pathname: string, baseUrl = DEFAULT_PUBLIC_SITE_URL) {
  return `${normalizeBaseUrl(baseUrl, DEFAULT_PUBLIC_SITE_URL)}${normalizePath(pathname)}`;
}
